// ABOUTME: Color picker popover for text and highlight colors in rich text editor.
// ABOUTME: Provides preset colors matching the obsidian theme plus custom hex input.

import { useState, useRef, useEffect } from 'react';
import { Palette, Check } from 'lucide-react';

interface ColorPickerProps {
  value: string | undefined;
  onChange: (color: string) => void;
  label?: string;
  icon?: React.ReactNode;
}

const PRESET_COLORS = [
  { name: 'Default', value: '' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Coral', value: '#f43f5e' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Gray', value: '#6b7280' },
  { name: 'White', value: '#ffffff' },
  { name: 'Black', value: '#000000' },
];

export function ColorPicker({ value, onChange, label, icon }: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [customColor, setCustomColor] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleColorSelect = (color: string) => {
    onChange(color);
    setIsOpen(false);
  };

  const handleCustomColorSubmit = () => {
    if (customColor && /^#[0-9A-Fa-f]{6}$/.test(customColor)) {
      onChange(customColor);
      setCustomColor('');
      setIsOpen(false);
    }
  };

  const currentColor = value || '';
  const isPresetColor = PRESET_COLORS.some((c) => c.value === currentColor);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`p-1.5 rounded-sm flex items-center gap-1 transition-colors cursor-pointer ${
          isOpen ? 'bg-primary-600 text-white' : 'hover:bg-surface-100 text-surface-600'
        }`}
        title={label || 'Color'}
      >
        {icon || <Palette className="w-4 h-4" />}
        {currentColor && (
          <div
            className="w-3 h-3 rounded-full border border-surface-300"
            style={{ backgroundColor: currentColor }}
          />
        )}
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 p-3 rounded-sm bg-surface-0 border border-surface-200 shadow-dropdown z-50 min-w-[200px]">
          {label && (
            <div className="text-xs text-surface-500 mb-2">{label}</div>
          )}

          {/* Preset Colors Grid */}
          <div className="grid grid-cols-6 gap-1.5 mb-3">
            {PRESET_COLORS.map((color) => (
              <button
                key={color.name}
                type="button"
                onClick={() => handleColorSelect(color.value)}
                className={`w-6 h-6 rounded-sm border transition-all flex items-center justify-center cursor-pointer ${
                  currentColor === color.value
                    ? 'ring-2 ring-primary-600 ring-offset-1 ring-offset-surface-0'
                    : 'hover:scale-110'
                } ${
                  color.value === ''
                    ? 'border-surface-200 bg-gradient-to-br from-surface-100 to-transparent'
                    : 'border-surface-200'
                }`}
                style={color.value ? { backgroundColor: color.value } : undefined}
                title={color.name}
              >
                {currentColor === color.value && color.value !== '' && (
                  <Check className="w-3 h-3 text-white drop-shadow-md" />
                )}
                {color.value === '' && currentColor === '' && (
                  <Check className="w-3 h-3 text-surface-600" />
                )}
              </button>
            ))}
          </div>

          {/* Custom Color Input */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="#000000"
              value={customColor}
              onChange={(e) => setCustomColor(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCustomColorSubmit();
                }
              }}
              className="flex-1 px-2 py-1 text-xs bg-surface-0 border border-surface-200 rounded-sm text-surface-800 placeholder:text-surface-400 focus:outline-none focus:ring-1 focus:ring-primary-600/25"
            />
            <button
              type="button"
              onClick={handleCustomColorSubmit}
              disabled={!customColor || !/^#[0-9A-Fa-f]{6}$/.test(customColor)}
              className="px-2 py-1 text-xs rounded-sm bg-primary-600 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-700 transition-colors cursor-pointer"
            >
              Apply
            </button>
          </div>

          {/* Show non-preset color if selected */}
          {currentColor && !isPresetColor && (
            <div className="mt-2 text-xs text-surface-500 flex items-center gap-2">
              <div
                className="w-4 h-4 rounded-sm border border-surface-200"
                style={{ backgroundColor: currentColor }}
              />
              Current: {currentColor}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
